/**
 * VÉRAULT Portal — API client.
 * Same axios instance pattern as the rest of the app, but exported from
 * the portal namespace so we never reach into legacy code by accident.
 */
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
  // Wave 12.d — Generous timeout so cold-start latency (≈ 5–15s on a
  // dormant Emergent pod) doesn't surface as "couldn't save" toasts.
  // Combined with the keep-alive hook + retry interceptor below.
  timeout: 30000,
});

// Wave 30.3 · Phase 9b — Optimistic concurrency guard.
// When a write request collides with a newer server version we receive
// a 409 with the agreed copy embedded in `detail.message`. Surface it
// once, calmly, via Sonner — UI components can still inspect the
// rejected promise for their own follow-up logic.
let _lastConflictToastAt = 0;
const CONFLICT_TOAST_DEDUP_MS = 2500;

// Wave 30.6 · Phase 11d — Calm 5xx fallback.
// Hospitality-first: one quiet toast per kind per 60s. Never panic.
// Never show stack traces. Read-only GET errors are silent (the page
// renders its own empty state); only mutating verbs surface a toast.
const SERVER_ERROR_TOAST_DEDUP_MS = 60000;
const _serverErrorToastAt = { server: 0, network: 0, timeout: 0 };
const _serverErrorCopy = {
  server: {
    msg: "Something's quiet on our end.",
    desc: "We'll keep trying. Your work is safe.",
  },
  network: {
    msg: "A momentary lapse in connection.",
    desc: "Reconnect and try once more.",
  },
  timeout: {
    msg: "The request is taking longer than usual.",
    desc: "Please try again in a moment.",
  },
};

function _maybeServerErrorToast(kind) {
  const now = Date.now();
  if (now - _serverErrorToastAt[kind] < SERVER_ERROR_TOAST_DEDUP_MS) return;
  _serverErrorToastAt[kind] = now;
  const copy = _serverErrorCopy[kind] || _serverErrorCopy.server;
  toast(copy.msg, { description: copy.desc, duration: 5800 });
}

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const status = err?.response?.status;
    const method = (err?.config?.method || "get").toLowerCase();
    const original = err?.config;

    // Wave 12.b — Silent refresh on 401. If the access cookie expired
    // mid-session we mint a fresh one and replay the request once. The
    // `_retry` flag prevents infinite loops if /refresh itself 401s.
    if (status === 401 && original && !original._retry && !/\/auth\/(refresh|login|me)/.test(original.url || "")) {
      original._retry = true;
      try {
        await api.post("/auth/refresh");
        return api(original);
      } catch {
        // Refresh failed — let the original 401 bubble up so the
        // caller can route to /login.
      }
    }

    // Wave 12.d — Cold-start / transient-failure retry.
    // The Emergent autoscaler can leave the backend pod cold; the first
    // request after a long idle pays a 5–15s cost that sometimes
    // surfaces as a timeout OR a 502/503/504. Retry ONCE with a small
    // delay so the user's save lands on the warmed pod transparently.
    const isColdStartCandidate =
      original &&
      !original._coldRetry &&
      !/\/auth\/(refresh|login|me|logout)/.test(original.url || "") &&
      (
        err?.code === "ECONNABORTED" ||                 // axios timeout
        (!err?.response && err?.message === "Network Error") ||
        status === 502 || status === 503 || status === 504
      );
    if (isColdStartCandidate) {
      original._coldRetry = true;
      await new Promise((r) => setTimeout(r, 1500));
      try {
        return await api(original);
      } catch {
        // fall through to normal error handling
      }
    }

    if (status === 409) {
      const detail = err.response.data?.detail;
      const message =
        (detail && typeof detail === "object" && detail.message) ||
        (typeof detail === "string" ? detail : "") ||
        "An update arrived while you were here — please refresh to see the latest.";
      const now = Date.now();
      if (now - _lastConflictToastAt > CONFLICT_TOAST_DEDUP_MS) {
        _lastConflictToastAt = now;
        toast(message, {
          description: "Refresh to load the newest version.",
          duration: 6200,
        });
      }
    } else if (status >= 500 && status <= 599) {
      // Only toast on writes — read failures are absorbed by the page's
      // own empty state, never panic the user mid-browse.
      if (method !== "get") _maybeServerErrorToast("server");
    } else if (!err?.response && err?.code === "ECONNABORTED") {
      if (method !== "get") _maybeServerErrorToast("timeout");
    } else if (!err?.response) {
      // Network failure (offline or DNS hiccup).
      if (method !== "get") _maybeServerErrorToast("network");
    }
    return Promise.reject(err);
  }
);

/**
 * Helper to attach an If-Match header for optimistic concurrency on
 * trip-level writes. Pass `trip.version` (number) — silently no-ops
 * when version is missing so legacy callers still work.
 */
export function ifMatchHeaders(version) {
  if (version === undefined || version === null) return {};
  return { "If-Match": String(version) };
}
